import './companyWideActivityWidget.less'
import {
  BarChartData,
  getActualTimeRangeFromGroupingInterval,
  getAggregatedCountByFeature,
  getPredictedTime,
  transformWidgetData
} from './widgetsUtil'
import { useGroupingIntervalOptions } from './widgetFilters'
import ChartBar from '../../../../../components/ChartBarSimple/chartBarSimple'
import {
  ACTION_FETCH_COMPANY_WIDE_ACTIVITY_WIDGET,
  fetchCompanyWideActivityWidget,
  resetCompanyWideActivityWidget
} from '../../slice'
import { useReduxSelector } from '../../../../../hooks/useReduxSelector'
import useUrlContext from '../../../../../hooks/useUrlContext'
import {
  ACTIVITY_FEATURE_COLOR_MAP,
  ACTIVITY_LOG_GROUP_TYPE,
  ActivityLogGroupingInterval,
  DATA_SOURCE_ID,
  FILTER_ACTIVITY_EVENT_TYPE,
  FILTER_DATE_CUSTOM_KEY,
  URL_ACCESS_CONTROL,
  URL_ACTIVITIES_LOG
} from '../../../../../constants'
import ChartLabel from '../../components/chartLabel'
import { ChartColors } from '../../constants'
import { ActivityEventType } from '../../../../accessControl/accessControlSlice'
import { getUpdatedUrl } from '../../../../../utils/urlUtil'
import historyService from '../../../../../services/historyService'
import { getShowLoader } from '../../../../../reducers/requestReducer'
import LoaderView from '../../../../../components/Loader/loader'
import { FILTER_SINGLE_DATE } from '../../../../../components/filters/hooks/useSingleDateFilter'
import CustomTooltip from '../../../../../components/ChartTooltipV2'
import { formatDate } from '../../../../../utils/timeUtil'
import { useIsFeatureFlagEnabled } from '../../../../../configs/useIsFeatureFlagEnabled'
import { Divider } from 'semantic-ui-react'
import { Icon, Select, Tooltip, Typography } from '@lightbeamai/design-system'
import dayjs from 'dayjs'
import { useDispatch } from 'react-redux'
import utc from 'dayjs/plugin/utc'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

dayjs.extend(utc)

const SENSITIVITY = {
  ALL: 'all',
  SENSITIVE: 'sensitive'
} as const
// TODO: Only 'All Data' is supported now, will enable 'Sensitive Data' option when BE implemented
// type Sensitivity = keyof typeof SENSITIVITY
type SensitivityValue = typeof SENSITIVITY[keyof typeof SENSITIVITY]
// const SENSITIVITY_LABELS: Record<Sensitivity, string> = {
//   ALL: 'All Data',
//   SENSITIVE: 'Sensitive Data'
// }

// const getSensitivityOptions = () => {
//   return (Object.keys(SENSITIVITY) as Sensitivity[]).map((level) => ({
//     key: SENSITIVITY[level],
//     value: SENSITIVITY[level],
//     label: SENSITIVITY_LABELS[level]
//   }))
// }

type CompanyWideActivityWidgetProps = {
  widgetDatasourceId?: string
}

const CompanyWideActivityWidget = (props: CompanyWideActivityWidgetProps) => {
  const { widgetDatasourceId } = props

  const context = useUrlContext()
  const pageUrl = `${context.listUrl}/${context.sourceId}`
  const datasourceId = widgetDatasourceId ?? (context.contextFilters[DATA_SOURCE_ID] || '')

  const isUebaEnabled = useIsFeatureFlagEnabled('uebaEnabled')

  const intl = useIntl()

  const dispatch = useDispatch()

  const [selectedSensitivity] = useState<SensitivityValue>(SENSITIVITY.ALL)
  const [groupingInterval, setGroupingInterval] = useState<ActivityLogGroupingInterval>(
    ActivityLogGroupingInterval.hourly
  )
  const [selectedFeature, setSelectedFeature] = useState<ActivityEventType>(ActivityEventType.all)

  const groupingIntervalOptions = useGroupingIntervalOptions()
  // const sensitivityOptions = getSensitivityOptions()

  const widgetData = useReduxSelector(
    (state) => state.customDashboards.companyWideActivityWidgetData ?? []
  )

  const showLoader = useReduxSelector(() =>
    getShowLoader(ACTION_FETCH_COMPANY_WIDE_ACTIVITY_WIDGET)
  )

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  useEffect(() => {
    if (!datasourceId) return

    const actualTimeRange = getActualTimeRangeFromGroupingInterval(groupingInterval)
    const predictedTimeRange = isUebaEnabled
      ? getPredictedTime(actualTimeRange, groupingInterval)
      : undefined

    const params = {
      datasourceId,
      actualTimeRange,
      predictedTimeRange,
      groupingInterval,
      timezone,
      features: [
        ActivityEventType.read,
        ActivityEventType.write,
        ActivityEventType.delete,
        ActivityEventType.download,
        ActivityEventType.create,
        ActivityEventType.all
      ],
      isSensitive: selectedSensitivity === SENSITIVITY.SENSITIVE
    }
    dispatch(fetchCompanyWideActivityWidget(params))

    return () => {
      dispatch(resetCompanyWideActivityWidget())
    }
  }, [
    dispatch,
    selectedFeature,
    groupingInterval,
    selectedSensitivity,
    datasourceId,
    timezone,
    isUebaEnabled
  ])

  // const handleSelectSensitivity = (_, data) => {
  //   setSelectedSensitivity(data.value)
  // }

  const handleIntervalChange = (_, data) => {
    setGroupingInterval(data.value)
  }

  const areAllFeaturesSelected = selectedFeature === ActivityEventType.all

  const handleBarClick = (data: BarChartData & { tooltipPayload: any }) => {
    const isPrediction = chartData.find((it) => it.name === data.name)?.isPrediction

    if (isPrediction) return

    const { tooltipPayload, startTimestamp, endTimestamp } = data
    const featureType = tooltipPayload?.[0]?.dataKey ?? selectedFeature

    const eventTypes: ActivityEventType[] = areAllFeaturesSelected
      ? [
          ActivityEventType.read,
          ActivityEventType.write,
          ActivityEventType.delete,
          ActivityEventType.download,
          ActivityEventType.create
        ]
      : [featureType]

    const dateRange = `${FILTER_DATE_CUSTOM_KEY},${dayjs(startTimestamp).format(
      'YYYY-MM-DD'
    )},${dayjs(
      groupingInterval === ActivityLogGroupingInterval.weekly ? endTimestamp : startTimestamp
    ).format('YYYY-MM-DD')}`

    const activityLogUrlWithFilters = getUpdatedUrl({
      pageName: URL_ACTIVITIES_LOG,
      pathName: `${pageUrl}${URL_ACCESS_CONTROL}${URL_ACTIVITIES_LOG}`,
      pageQueryParams: {
        [FILTER_ACTIVITY_EVENT_TYPE]: eventTypes.join(','),
        [ACTIVITY_LOG_GROUP_TYPE]: groupingInterval,
        [FILTER_SINGLE_DATE]: dateRange
      }
    })

    historyService.push(activityLogUrlWithFilters)
  }

  const aggregatedTotal = getAggregatedCountByFeature({
    widgetData,
    isSensitive: selectedSensitivity === SENSITIVITY.SENSITIVE
  })
  const chartData = useMemo(() => {
    return transformWidgetData({
      widgetData,
      selectedFeature,
      isSensitive: selectedSensitivity === SENSITIVITY.SENSITIVE,
      groupingInterval
    })
  }, [groupingInterval, selectedFeature, selectedSensitivity, widgetData])

  const countsFormatter = useCallback(
    (value) => intl.formatNumber(value, { notation: 'compact' }),
    [intl]
  )

  const getPayloadLabelByDataKey = useCallback(
    (eventType: ActivityEventType | string) => {
      // ? Not all ActivityEventType are displayed in the bar chart
      const evenTypeToLabel: Omit<
        Record<ActivityEventType, string>,
        ActivityEventType.all | ActivityEventType.uniqueRead | ActivityEventType.uniqueWrite
      > = {
        [ActivityEventType.read]: intl.formatMessage({
          id: 'dataSources.widget.companyWideActivity.chart.label.read'
        }),
        [ActivityEventType.write]: intl.formatMessage({
          id: 'dataSources.widget.companyWideActivity.chart.label.write'
        }),
        [ActivityEventType.delete]: intl.formatMessage({
          id: 'dataSources.widget.companyWideActivity.chart.label.delete'
        }),
        [ActivityEventType.download]: intl.formatMessage({
          id: 'dataSources.widget.companyWideActivity.chart.label.download'
        }),
        [ActivityEventType.create]: intl.formatMessage({
          id: 'dataSources.widget.companyWideActivity.chart.label.create'
        })
      }

      return evenTypeToLabel[eventType]
    },
    [intl]
  )

  const barColors = useMemo(() => {
    const featureNames = areAllFeaturesSelected
      ? [
          ActivityEventType.read,
          ActivityEventType.write,
          ActivityEventType.delete,
          ActivityEventType.download,
          ActivityEventType.create
        ]
      : [selectedFeature]

    return featureNames.map((feature, featureIndex) => ({
      key: feature,
      color: ACTIVITY_FEATURE_COLOR_MAP[feature],
      stackId: 'stack',
      // Mark the last data point as stripped (for prediction) - requires isUebaEnabled
      renderCell: (index) => {
        return isUebaEnabled
          ? {
              fill: chartData[index]?.isPrediction
                ? `url(#barStripes-${feature})`
                : ACTIVITY_FEATURE_COLOR_MAP[feature]
            }
          : {
              fill: ACTIVITY_FEATURE_COLOR_MAP[feature]
            }
      },
      // Only show label on the last feature
      // (for prediction - topmost bar in the stack) - requires isUebaEnabled
      labelRenderer:
        isUebaEnabled && featureIndex === featureNames.length - 1
          ? (index) => {
              return index === chartData[index]?.isPrediction
                ? intl.formatMessage({
                    id: 'dataSources.widget.companyWideActivity.chart.prediction'
                  })
                : null
            }
          : undefined
    }))
  }, [areAllFeaturesSelected, intl, selectedFeature, isUebaEnabled, chartData])

  const renderData = useMemo(() => {
    return chartData.map(({ isPrediction: _, ...it }) => ({
      ...it
    }))
  }, [chartData])

  return (
    <div className="lb-widget-card company-wide-activity-widget xs-p-16">
      <div className="chart-title">
        <div className="align-center">
          <Typography variant="h6" className="xs-mb-0 xs-mr-8">
            {intl.formatMessage({ id: 'dataSources.widget.companyWideActivity.title' })}
          </Typography>
          <Tooltip
            title={intl.formatMessage({
              id: 'dataSources.widget.companyWideActivity.title.tooltip'
            })}
            placement="top"
          >
            <Icon name="AlertCircle" size={16} className="pointer" />
          </Tooltip>
        </div>
        <div className="title-options">
          {/* <Select
            className="select-data-sensitivity"
            options={sensitivityOptions}
            onChange={handleSelectSensitivity}
            value={selectedSensitivity}
          /> */}
          <Select
            className="select-data-duration"
            options={groupingIntervalOptions}
            onChange={handleIntervalChange}
            value={groupingInterval}
          />
        </div>
      </div>
      <Divider />
      <div className="chart-labels">
        <ChartLabel
          count={aggregatedTotal.TOTAL_EVENTS}
          text={intl.formatMessage({
            id: 'dataSources.widget.companyWideActivity.chart.label.total'
          })}
          isSelected={selectedFeature === ActivityEventType.all}
          lineColor={ACTIVITY_FEATURE_COLOR_MAP[ActivityEventType.all]}
          onClick={() => setSelectedFeature(ActivityEventType.all)}
        />
        <ChartLabel
          count={aggregatedTotal.CREATE_COUNT}
          text={intl.formatMessage({
            id: 'dataSources.widget.companyWideActivity.chart.label.create'
          })}
          isSelected={selectedFeature === ActivityEventType.create}
          lineColor={ACTIVITY_FEATURE_COLOR_MAP[ActivityEventType.create]}
          onClick={() => setSelectedFeature(ActivityEventType.create)}
        />
        <ChartLabel
          count={aggregatedTotal.READ_COUNT}
          text={intl.formatMessage({
            id: 'dataSources.widget.companyWideActivity.chart.label.read'
          })}
          isSelected={selectedFeature === ActivityEventType.read}
          lineColor={ACTIVITY_FEATURE_COLOR_MAP[ActivityEventType.read]}
          onClick={() => setSelectedFeature(ActivityEventType.read)}
        />
        <ChartLabel
          count={aggregatedTotal.WRITE_COUNT}
          text={intl.formatMessage({
            id: 'dataSources.widget.companyWideActivity.chart.label.write'
          })}
          isSelected={selectedFeature === ActivityEventType.write}
          lineColor={ACTIVITY_FEATURE_COLOR_MAP[ActivityEventType.write]}
          onClick={() => setSelectedFeature(ActivityEventType.write)}
        />
        <ChartLabel
          count={aggregatedTotal.DOWNLOAD_COUNT}
          text={intl.formatMessage({
            id: 'dataSources.widget.companyWideActivity.chart.label.download'
          })}
          isSelected={selectedFeature === ActivityEventType.download}
          lineColor={ACTIVITY_FEATURE_COLOR_MAP[ActivityEventType.download]}
          onClick={() => setSelectedFeature(ActivityEventType.download)}
        />
        <ChartLabel
          count={aggregatedTotal.DELETE_COUNT}
          text={intl.formatMessage({
            id: 'dataSources.widget.companyWideActivity.chart.label.delete'
          })}
          isSelected={selectedFeature === ActivityEventType.delete}
          lineColor={ACTIVITY_FEATURE_COLOR_MAP[ActivityEventType.delete]}
          onClick={() => setSelectedFeature(ActivityEventType.delete)}
        />
      </div>
      <div className="charts-wrapper">
        <ChartBar
          className="xs-mt-24"
          data={renderData}
          cartesianGridHorrizontal
          xTickFormatter={({ x, y, payload }) => {
            const record = chartData[payload.index]

            return (
              <g transform={`translate(${x},${y})`}>
                <text
                  x={0}
                  y={0}
                  // dy={16}
                  textAnchor="middle"
                  fill={ChartColors.SecondaryText}
                  fontSize={
                    groupingInterval === ActivityLogGroupingInterval.hourly ? '0.68rem' : '0.72rem'
                  }
                >
                  <tspan textAnchor="middle" x="0">
                    {payload.value}
                  </tspan>
                  {record?.startTimestamp &&
                    groupingInterval === ActivityLogGroupingInterval.daily && (
                      <tspan textAnchor="middle" x="0" dy="1.2em">
                        {dayjs(record?.startTimestamp).format('DD/MM')}
                      </tspan>
                    )}
                </text>
              </g>
            )
          }}
          chartResponsiveHeight={200}
          barSize={60}
          yLabel={intl.formatMessage({
            id: 'dataSources.widget.companyWideActivity.chart.yAxis.label'
          })}
          barColors={barColors}
          xAxisAngle={0}
          YTickFormatter={countsFormatter}
          yAxisLabelPadding={{ dx: -12, dy: -6 }}
          customToolTip={
            <CustomTooltip
              getTooltipLabelByDataKey={(
                params: Array<{ payload?: { startTimestamp: string; endTimestamp: string } }>
              ) => {
                const payload = params?.[0]?.payload
                if (!payload?.startTimestamp || !payload?.endTimestamp) {
                  return undefined
                }

                switch (groupingInterval) {
                  case ActivityLogGroupingInterval.hourly:
                    return `${dayjs(payload.startTimestamp).format('h A')} - ${dayjs(
                      payload.startTimestamp
                    ).format('MMM DD, YYYY')}`
                  case ActivityLogGroupingInterval.daily:
                    return `${dayjs(payload.startTimestamp).format('dddd')} - ${dayjs(
                      payload.startTimestamp
                    ).format('MMM DD, YYYY')}`
                  case ActivityLogGroupingInterval.weekly:
                    return `${formatDate(payload.startTimestamp)} - ${dayjs(
                      payload.endTimestamp
                    ).format('MMM DD, YYYY')}`
                }
              }}
              getPayloadLabelByDataKey={getPayloadLabelByDataKey}
            />
          }
          onBarItemClick={handleBarClick}
          tickMargin={4}
        />
      </div>
      <LoaderView size="small" showLoader={showLoader} />
    </div>
  )
}

export default CompanyWideActivityWidget
